// @ts-strict-ignore
import { Injectable, inject } from '@angular/core';
import { ErrorObject } from '@app/_shared/interfaces';
import { SubscriptionsService } from '@app/_shared/service';
import { SnackBarService } from '@app/_shared/snack-bars/snack-bar.service';
import { getStoreName } from '@app/main-store/bootstrap.selector';
import { selectRouteParam } from '@app/main-store/router.selector';
import { SubscriptionsStore } from '@app/settings/store-settings/general/subscriptions/store/subscriptions.signal-store';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import * as subscriptionActions from './subscription.actions';

@Injectable()
export class SubscriptionEffects {
  private actions$ = inject(Actions);
  private subscriptionService = inject(SubscriptionsService);
  private subscriptionsStore = inject(SubscriptionsStore);
  private snackBarService = inject(SnackBarService);
  private translocoService = inject(TranslocoService);
  private rxStore = inject(Store);

  updateSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(subscriptionActions.updateSubscription),
      switchMap((action) =>
        this.subscriptionService.patch(action.data).pipe(
          map((response) => subscriptionActions.updateSubscriptionSuccess({ response: response.data })),
          catchError((error) => of(subscriptionActions.updateSubscriptionFailed({ error: error.error }))),
        ),
      ),
    );
  });

  refreshSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(subscriptionActions.refreshSubscription),
      switchMap((action) =>
        this.subscriptionService.show(action.subscriptionId).pipe(
          map((response) => subscriptionActions.refreshSubscriptionSuccess({ response: response.data })),
          catchError((error) => of(subscriptionActions.refreshSubscriptionFailed({ error: error.error }))),
        ),
      ),
    );
  });

  globalErrorNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionFailed, subscriptionActions.createSubscriptionFailed),
        tap((action) =>
          this.snackBarService.openFailure({
            body: getErrorMessage(action.error, this.translocoService),
          }),
        ),
      ),
    { dispatch: false },
  );

  updatePostingCreditsOnSubscriptionUpdate = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionSuccess),
        // Root store data holds one store at a time: refresh only for the on-screen store, else its in-flight requests get overwritten.
        concatLatestFrom(() => this.rxStore.select(selectRouteParam('storeId'))),
        filter(([action]) => action.response.attributes.billing_driver !== 'shopify'),
        tap(([action, routeStoreId]) => {
          const storeId = action.response.attributes.store_id;
          if (routeStoreId !== undefined && +routeStoreId !== storeId) return;
          this.subscriptionsStore.getPostingCredits(storeId);
          this.subscriptionsStore.getExternalSubscription(storeId);
        }),
      ),
    { dispatch: false },
  );

  updateSubscriptionNotification$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionSuccess, subscriptionActions.createSubscriptionSuccess),
        // The user may have switched store meanwhile: name the store the action was for.
        concatLatestFrom((action) => this.rxStore.select(getStoreName(action.response.attributes.store_id))),
        tap(([, storeName]) => {
          this.snackBarService.openSucceeded({
            body: this.translocoService.translate('subscriptions.messages.subscriptionUpdated', { storeName }),
          });
        }),
      );
    },
    { dispatch: false },
  );

  createSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(subscriptionActions.createSubscription),
      switchMap((action) =>
        this.subscriptionService.post(action.data).pipe(
          map((response) => subscriptionActions.createSubscriptionSuccess({ response: response.data })),
          catchError((error) => of(subscriptionActions.createSubscriptionFailed({ error: error.error }))),
        ),
      ),
    );
  });

  goToConfirmationUrlOnUpdateSubscriptionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionSuccess, subscriptionActions.createSubscriptionSuccess),
        filter(
          (action) =>
            action.response.attributes.billing_driver === 'shopify' &&
            action.response.attributes.confirmation_url !== null,
        ),
        tap((action) => (window.location.href = action.response.attributes.confirmation_url)),
      ),
    { dispatch: false },
  );
}

function getErrorMessage(error: ErrorObject, translocoService: TranslocoService): string {
  if (error?.code) return error.message;

  if (error?.error_codes?.['promotion_code']?.includes('exists'))
    return translocoService.translate('general.messages.promotion_code_invalid');

  return translocoService.translate('general.messages.unexpected_error');
}
